import { inputBaseClasses } from '@mui/material/InputBase'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Paper, TextField } from '../../../components/generic.tsx'
import { Icon } from '../../../components/Icon.tsx'
import { useImpersonation, useImpersonationActions } from '../../../data/impersonation.ts'
import { useCustomers, useProducts } from '../../../data/service.ts'
import { ids } from '../../../ids.ts'
import { SubscriptionStatusIcon } from './subscriptionUtils.tsx'

export const ImpersonationSelect = () => {
	const { t } = useTranslation()
	const { data: customers } = useCustomers()
	const { data: products } = useProducts()
	const impersonation = useImpersonation()
	const { startImpersonating, stopImpersonating } = useImpersonationActions()
	const customerRecord = useMemo(
		() =>
			(customers ?? []).reduce(
				(acc, customer) => {
					acc[customer.id] = customer
					return acc
				},
				{} as Record<string, NonNullable<typeof customers>[number]>,
			),
		[customers],
	)

	const options = useMemo(() => {
		return (
			(products ?? [])
				?.filter((product) => product.status === 'Active')
				.map((product) => {
					const customerName = customerRecord[product.customerId]?.name
					return {
						label: `${customerName} (${product.name})`,
						product,
					}
				}) ?? []
		)
	}, [products, customerRecord])

	const selectedValue = useMemo(() => {
		if (!impersonation?.subscriptionId || options.length === 0) return null
		return options.find((option) => option.product.id === impersonation.subscriptionId) ?? null
	}, [options, impersonation])

	return (
		<Paper elevation={5} data-ta={ids.smp.impersonate.dropDown} sx={{ w: 340, h: 48, border: 1, borderColor: 'divider' }}>
			<Autocomplete
				sx={{ h: '100%' }}
				value={selectedValue}
				isOptionEqualToValue={(option, value) => option?.product?.id === value?.product?.id}
				options={options}
				renderInput={(params) => {
					const { InputProps, ...rest } = params
					InputProps.startAdornment = <Icon name='MagnifyingGlass' sx={{ fontSize: 16 }} />
					return (
						<TextField
							{...params}
							{...rest}
							placeholder={t(($) => $.impersonation.selectAccount)}
							sx={{ h: '100%', [`.${inputBaseClasses.root}`]: { h: '100%' } }}
						/>
					)
				}}
				getOptionLabel={(option) => option.label}
				onChange={async (_, value) => {
					if (value) {
						await startImpersonating(value.product.id)
					} else {
						await stopImpersonating()
					}
				}}
				renderOption={(props, value) => {
					const { key: _key, ...rest } = props
					return (
						<li key={value.product.id} {...rest} style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
							<SubscriptionStatusIcon status={value.product.status} trial={value.product.recurringPeriod === 'Trial'} /> {value.label}
						</li>
					)
				}}
			/>
		</Paper>
	)
}
